// A round picture of a person for system notifications (DMs, thread replies, comments, reactions):
// their uploaded photo, or their initials on their color like the in-app Avatar. Returned as a PNG
// data URL (what both browser notifications and the desktop app accept) and cached per look.
import type { User } from '@shared/types';
import { initials } from './hierarchy';
import { readableOn } from './color';

const SIZE = 128;
const cache = new Map<string, Promise<string | undefined>>();

async function loadPhoto(url: string): Promise<ImageBitmap | null> {
  try {
    const res = await fetch(url, { credentials: 'same-origin' });
    return res.ok ? await createImageBitmap(await res.blob()) : null;
  } catch {
    return null; // another site's image without CORS, or not an image: fall back to initials
  }
}

function draw(user: User, photo: ImageBitmap | null) {
  const c = document.createElement('canvas');
  c.width = c.height = SIZE;
  const ctx = c.getContext('2d')!;
  ctx.beginPath();
  ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2);
  ctx.clip();
  if (photo) {
    const side = Math.min(photo.width, photo.height);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(photo, (photo.width - side) / 2, (photo.height - side) / 2, side, side, 0, 0, SIZE, SIZE);
  } else {
    const bg = user.color || '#656f7d';
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, SIZE, SIZE);
    ctx.fillStyle = readableOn(bg);
    ctx.font = `600 ${Math.round(SIZE * 0.4)}px Inter, -apple-system, "Segoe UI", sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials(user.name), SIZE / 2, SIZE / 2 + SIZE * 0.03);
  }
  return c.toDataURL('image/png');
}

export function personIcon(user: User | undefined): Promise<string | undefined> {
  if (!user) return Promise.resolve(undefined);
  const key = [user.id, user.avatarUrl ?? '', user.name, user.color].join('|');
  let icon = cache.get(key);
  if (!icon) {
    icon = (async () => draw(user, user.avatarUrl ? await loadPhoto(user.avatarUrl) : null))().catch(() => undefined);
    cache.set(key, icon);
  }
  return icon;
}
